import { RICH_TEXT_FONT_OPTIONS } from "@/fontFamily";

/** Font-family options sourced from the shared editor font list (src/fontFamily.ts). */
const FONT_FAMILY_OPTIONS = RICH_TEXT_FONT_OPTIONS.filter(
  (opt) => opt.id && opt.cssValue,
).map((opt) => ({ label: opt.label, value: opt.cssValue }));

const DEFAULT_FONT_FAMILY = "var(--font-poppins), Poppins, sans-serif";

const color = (
  fieldName: string,
  label: string,
  defaultValue: string,
  extra: Record<string, unknown> = {},
) => ({
  fieldName,
  label,
  value: defaultValue,
  valueType: "string",
  filedType: "COLOR",
  placeholder: defaultValue,
  required: false,
  defaultValue,
  ...extra,
});

const text = (
  fieldName: string,
  label: string,
  defaultValue: string,
  placeholder: string,
  extra: Record<string, unknown> = {},
) => ({
  fieldName,
  label,
  value: defaultValue,
  valueType: "string",
  filedType: "INPUT",
  placeholder,
  required: false,
  defaultValue,
  ...extra,
});

const icon = (
  fieldName: string,
  label: string,
  placeholder: string,
  extra: Record<string, unknown> = {},
) => ({
  fieldName,
  label,
  value: "",
  valueType: "string",
  filedType: "FILE",
  placeholder,
  required: false,
  defaultValue: "",
  accept: "image/png,image/jpeg,image/webp,image/svg+xml",
  ...extra,
});

const toggle = (
  fieldName: string,
  label: string,
  placeholder: string,
  defaultValue: boolean,
) => ({
  fieldName,
  label,
  value: defaultValue,
  valueType: "boolean",
  filedType: "TOGGLE",
  placeholder,
  required: false,
  defaultValue,
});

/**
 * Author Profile 1 (section 3002 / variant 3000).
 *
 * The author dropdown is the ONLY content control — every author detail (name,
 * role, bio, badges, socials, article count) is read live from the selected
 * author row, so nothing here duplicates it. Everything else is presentation.
 */
export const AUTHOR_PROFILE_1_CONSTANTS = {
  METADATA_TITLES: {
    metadata: "Breadcrumb items",
  },

  DEFAULT_ROW: {
    id: "1",
    meta_label: "Home",
    meta_url: "/",
    meta_priority: 1,
    meta_status: true,
  },

  AUTHOR_PROFILE_FIELDS_CONFIG: [
    {
      fieldName: "author_id",
      label: "Author",
      value: "",
      valueType: "string",
      filedType: "SELECT",
      placeholder: "Select an author",
      description:
        "Only published authors are listed. All details are pulled from Author Management — edit the author there to update this widget.",
      required: true,
      defaultValue: "",
      className: "h-9 py-1.5 text-sm",
      iconClassName: "!size-4",
      options: [],
      dynamicOptions: "authors",
      validation: (value: any) => {
        if (value == null || String(value).trim() === "") {
          return "Select an author";
        }
        return null;
      },
    },

    // ── Section shell ────────────────────────────────────────────────────────
    {
      fieldName: "font_family",
      label: "Font Family",
      value: DEFAULT_FONT_FAMILY,
      valueType: "string",
      filedType: "SELECT",
      placeholder: "Select font family",
      required: false,
      defaultValue: DEFAULT_FONT_FAMILY,
      className: "h-9 py-1.5 text-sm",
      iconClassName: "!size-4",
      options: FONT_FAMILY_OPTIONS,
    },
    toggle(
      "use_gradient_background",
      "Use Gradient Background",
      "Off uses the flat background color instead",
      true,
    ),
    color("background_color", "Background Color", "#FFFFFF"),
    text("gradient_angle", "Gradient Angle", "34deg", "e.g. 34deg", {
      dependsOn: "use_gradient_background",
    }),
    color(
      "gradient_start_color",
      "Gradient Start Color",
      "rgba(95, 78, 226, 0.1)",
      {
        dependsOn: "use_gradient_background",
      },
    ),
    color(
      "gradient_end_color",
      "Gradient End Color",
      "rgba(0, 212, 235, 0.1)",
      {
        dependsOn: "use_gradient_background",
      },
    ),

    // ── Breadcrumb ───────────────────────────────────────────────────────────
    toggle(
      "show_breadcrumb",
      "Show Breadcrumb",
      "Show the breadcrumb row",
      true,
    ),
    {
      fieldName: "breadcrumb_note",
      label: "Breadcrumb items",
      filedType: "TEXT_BLOCK",
      description:
        "Build the trail in the \"Breadcrumb items\" table below — nothing is added automatically. Leave a crumb's URL blank to render it as the current page (e.g. the author's name).",
      fullWidth: true,
      dependsOn: "show_breadcrumb",
    },
    color("breadcrumb_text_color", "Breadcrumb Link Color", "rgba(0,0,0,0.6)", {
      dependsOn: "show_breadcrumb",
    }),
    color("breadcrumb_active_color", "Breadcrumb Current Page Color", "#342B7C", {
      dependsOn: "show_breadcrumb",
    }),

    // ── Profile card ─────────────────────────────────────────────────────────
    color("card_background_color", "Card Background Color", "#FFFFFF"),
    color("card_border_color", "Card Border Color", "rgba(0,0,0,0.1)"),
    color("author_name_color", "Author Name Color", "#342B7C"),
    // One colour drives the two header sub-lines: the author's tagline and the
    // "892+ of articles published" count beneath it.
    color(
      "author_role_color",
      "Tagline & Article Count Color",
      "rgba(0,0,0,0.6)",
    ),
    color("badge_text_color", "Badge Text Color", "rgba(0,0,0,0.6)"),
    color("badge_border_color", "Badge Border Color", "rgba(0,0,0,0.2)"),
    color("badge_background_color", "Badge Background Color", "#FFFFFF"),
    text(
      "top_contributor_label",
      "Top Contributor Badge Label",
      "Top Contributor",
      "Top Contributor",
    ),
    icon(
      "top_contributor_icon_url",
      "Top Contributor Badge Icon",
      "Upload badge icon",
    ),
    text(
      "author_of_month_label",
      "Author of the Month Badge Label",
      "Author of the Month",
      "Author of the Month",
    ),
    icon(
      "author_of_month_icon_url",
      "Author of the Month Badge Icon",
      "Upload badge icon",
    ),
    text(
      "associated_prefix_label",
      "Associated Since Label",
      "Associated with upGrad since",
      "Associated with upGrad since",
    ),
    color(
      "associated_label_color",
      "Associated Label Color",
      "rgba(0,0,0,0.5)",
    ),
    color("associated_value_color", "Associated Date Color", "#000000"),
    color("divider_color", "Card Divider Color", "rgba(0,0,0,0.1)"),
    text("domain_label", "Domain Label", "Domain:", "Domain:"),
    text(
      "current_role_label",
      "Current Role Label",
      "Current role in the industry :",
      "Current role in the industry :",
    ),
    text(
      "education_label",
      "Education Qualification Label",
      "Education Qualification :",
      "Education Qualification :",
    ),
    text("expertise_label", "Expertise Label", "Expertise :", "Expertise :"),
    text(
      "tools_label",
      "Tools & Technologies Label",
      "Tools & Technologies :",
      "Tools & Technologies :",
    ),
    text(
      "certifications_label",
      "Certifications Label",
      "Certifications :",
      "Certifications :",
    ),
    // Renders after the count in the card header: "892+ of articles published".
    text(
      "articles_label",
      "Articles Published Label",
      "of articles published",
      "of articles published",
    ),
    text(
      "articles_count_suffix",
      "Articles Count Suffix",
      "+",
      "e.g. + (leave blank for none)",
    ),
    color("info_label_color", "Card Info Label Color", "rgba(0,0,0,0.6)"),
    color("info_value_color", "Card Info Value Color", "#000000"),
    // Expertise / Tools & Technologies / Certifications pills.
    color("chip_background_color", "Tag Background Color", "rgba(95, 78, 226, 0.1)"),
    color("chip_text_color", "Tag Text Color", "#000000"),

    // ── About column ─────────────────────────────────────────────────────────
    text("about_title", "About Card Title", "About", "About"),
    color("about_title_color", "About Title Color", "#342B7C"),
    color(
      "about_description_color",
      "About Description Color",
      "rgba(0,0,0,0.8)",
    ),
    text("follow_label", "Follow Label", "Follow me on :", "Follow me on :"),
    color("follow_label_color", "Follow Label Color", "#000000"),
    color("social_icon_background_color", "Social Icon Background", "#FFFFFF"),
    icon("linkedin_icon_url", "LinkedIn Icon", "Upload LinkedIn icon"),
    icon("quora_icon_url", "Quora Icon", "Upload Quora icon"),
    icon("twitter_icon_url", "Twitter Icon", "Upload Twitter icon"),
    icon("blog_icon_url", "Author's Blog Icon", "Upload blog icon"),

    // ── Blog list ────────────────────────────────────────────────────────────
    toggle(
      "show_blog_list",
      "Show Author's Blogs",
      "Show the Published / Most Popular blog list",
      true,
    ),
    color("blog_list_background_color", "Blog List Background", "#FFFFFF", {
      dependsOn: "show_blog_list",
    }),
    text(
      "published_tab_label",
      "Published Tab Label",
      "Published",
      "Published",
      {
        dependsOn: "show_blog_list",
      },
    ),
    text(
      "popular_tab_label",
      "Most Popular Tab Label",
      "Most Popular",
      "Most Popular",
      { dependsOn: "show_blog_list" },
    ),
    color("tab_text_color", "Tab Text Color", "rgba(0,0,0,0.6)", {
      dependsOn: "show_blog_list",
    }),
    color("tab_active_text_color", "Tab Active Text Color", "#342B7C", {
      dependsOn: "show_blog_list",
    }),
    color("tab_indicator_color", "Tab Indicator Color", "#342B7C", {
      dependsOn: "show_blog_list",
    }),
    color("blog_card_background_color", "Blog Card Background", "#FFFFFF", {
      dependsOn: "show_blog_list",
    }),
    color("blog_card_border_color", "Blog Card Border", "rgba(0,0,0,0.1)", {
      dependsOn: "show_blog_list",
    }),
    color("blog_card_title_color", "Blog Card Title Color", "#101112", {
      dependsOn: "show_blog_list",
    }),
    color("blog_card_date_color", "Blog Card Date Color", "rgba(0,0,0,0.6)", {
      dependsOn: "show_blog_list",
    }),
    icon("blog_card_date_icon_url", "Blog Card Date Icon", "Upload date icon", {
      dependsOn: "show_blog_list",
    }),
    text("read_more_label", "Read More Label", "Read More", "Read More", {
      dependsOn: "show_blog_list",
    }),
    color("read_more_text_color", "Read More Color", "#342B7C", {
      dependsOn: "show_blog_list",
    }),
    text("load_more_label", "Load More Label", "Load More", "Load More", {
      dependsOn: "show_blog_list",
    }),
    color("load_more_text_color", "Load More Text Color", "#342B7C", {
      dependsOn: "show_blog_list",
    }),
    color("load_more_background_color", "Load More Background", "#FFFFFF", {
      dependsOn: "show_blog_list",
    }),
    color("load_more_border_color", "Load More Border", "#342B7C", {
      dependsOn: "show_blog_list",
    }),

    {
      fieldName: "status",
      label: "Status",
      value: true,
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder: "Enable/Disable section",
      required: false,
      defaultValue: true,
    },
  ],

  METADATA_FIELDS: [
    {
      header: "Label",
      fieldName: "meta_label",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Home, Blog, author name, …",
    },
    {
      header: "URL",
      fieldName: "meta_url",
      value: "",
      valueType: "string",
      filedType: "INPUT",
      placeholder: "Leave empty on the last crumb (current page)",
    },
    {
      header: "Priority",
      fieldName: "meta_priority",
      value: "",
      valueType: "number",
      filedType: "INPUT",
      placeholder: "Order (left to right)",
      required: true,
    },
    {
      header: "Status",
      fieldName: "meta_status",
      value: "",
      valueType: "boolean",
      filedType: "TOGGLE",
      placeholder: "Visible",
      defaultValue: true,
    },
  ],
};
